import { Pagination } from '$core/models/pagination';
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Params } from '@angular/router';
import { Observable } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import {
  AddAttachmentBody,
  Batch,
  BatchMailing,
  BatchDepartureResp,
  BatchParseDto,
  BatchShort,
  DepartureAttachmentItem,
  DuplicateType,
  EditDepartureAttachmentParams,
  PrecalculateItemsResp,
  TrackingStep,
} from '../../models';
import { BatchesParseStorageService } from '../batches-parse-storage.service';
import { createHttpParams } from '$core/helpers/http-params';
import { BatchMailingIndexParams } from './batch-mailing-index-params';
import { BatchMailingShowParams } from './batch-mailing-show-params';
import { BelpostToShortContactAdapterService } from '$shared/components/address-widget-picker';
import { BatchCreateParams } from './batch-create-params';
import { BatchShowParams } from './batch-show-params';
import { BatchRemoveParams } from './batch-remove-params';
import { BatchMailingCreateParams } from './batch-mailing-create-params';
import { BatchMailingUpdateParams } from './batch-mailing-update-params';
import { LabelSizeList } from '../../models';
import { GenerateBatchDocumentParams } from './generate-batch-document-params';

@Injectable()
export class BatchesService {
  private readonly apiRoute = '/batch-mailing/list';

  constructor(
    private readonly http: HttpClient,
    private readonly batchesParseStorageService: BatchesParseStorageService,
    private readonly belpostToShortContactAdapterService: BelpostToShortContactAdapterService
  ) {}

  public index(params: Params): Observable<Pagination<BatchShort>> {
    return this.http.get<Pagination<BatchShort>>(`/v2${this.apiRoute}`, {
      params,
    });
  }

  public show(params: BatchShowParams): Observable<Batch> {
    const { batchId: id } = params;

    return this.http.get<Batch>(`/v2${this.apiRoute}/${id}`).pipe(
      map((res) => ({
        ...res,
        items: res.items.map((item, index) => ({
          ...item,
          number: index + 1,
        })),
      }))
    );
  }

  public create(params: BatchCreateParams): Observable<Batch> {
    const body: Record<string, unknown> = {
      direction: params.direction,
      is_declared_value: params.is_declared_value,
      is_partial_receipt: params.is_partial_receipt,
      is_priority: params.is_priority,
      negotiated_rate: params.negotiated_rate,
      payment_type: params.payment_type,
      postal_delivery_type: params.postal_delivery_type,
      postal_items_in_ops:
        params.postal_items_in_ops !== undefined
          ? params.postal_items_in_ops
          : undefined,
      card_number:
        params.card_number !== undefined ? params.card_number : undefined,
      is_document:
        params.is_document !== undefined ? params.is_document : undefined,
    };

    return this.http.post<Batch>(`/v2${this.apiRoute}`, body);
  }

  public updateBatch(batchId: number, params: Params): Observable<Batch> {
    return this.http.patch<Batch>(`/v2${this.apiRoute}/${batchId}`, params);
  }

  public remove(params: BatchRemoveParams): Observable<unknown> {
    const { batchId: id } = params;

    return this.http.delete(`/v2${this.apiRoute}/${id}`);
  }

  public batchMailingIndex(
    params: BatchMailingIndexParams
  ): Observable<BatchDepartureResp> {
    const { batchId, search, perPage, page } = params;
    return this.http
      .get<BatchDepartureResp>(`/v2${this.apiRoute}/${batchId}/item`, {
        params: createHttpParams({
          search: search ? search : undefined,
          per_page: perPage ? perPage : undefined,
          page: page ? page : undefined,
        }),
      })
      .pipe(
        map((response) => ({
          ...response,
          data: response.data.map((item, index) => ({
            ...item,
            number:
              index +
              1 +
              (response.meta.current_page - 1) * response.meta.per_page,
          })),
        }))
      );
  }

  public showMailing(params: BatchMailingShowParams): Observable<BatchMailing> {
    const { batchId, mailingId } = params;
    return this.http.get<BatchMailing>(
      `/v2${this.apiRoute}/${batchId}/item/${mailingId}`
    );
  }

  public createMailing(
    params: BatchMailingCreateParams
  ): Observable<BatchMailing> {
    const { batchId } = params;

    const body = {
      recipient_contact_widget_data:
        this.belpostToShortContactAdapterService.transform(
          params.recipient_contact_widget_data
        ),
      s10code: params.s10code,
      notification_s10code: params.notification_s10code,
      notification: params.notification,
      weight: params.weight,
      addons: params.addons,
      count: params.duplicates_count ? params.duplicates_count : 1,
    };

    return this.http.post<BatchMailing>(
      `/v2${this.apiRoute}/${batchId}/item`,
      body
    );
  }

  public updateMailing(
    params: BatchMailingUpdateParams
  ): Observable<BatchMailing> {
    const { batchId, mailingId } = params;

    const body = {
      recipient_contact_widget_data:
        this.belpostToShortContactAdapterService.transform(
          params.recipient_contact_widget_data
        ),
      s10code: params.s10code,
      notification_s10code: params.notification_s10code,
      notification: params.notification,
      weight: params.weight,
      addons: params.addons,
    };

    return this.http.put<BatchMailing>(
      `/v2${this.apiRoute}/${batchId}/item/${mailingId}`,
      body
    );
  }

  public removeMailings(batchId: number, MailingIds: number[]) {
    return this.http.post(`/v2${this.apiRoute}/${batchId}/item/batch-delete`, {
      ids: MailingIds,
    });
  }

  public removeBatchList(params: Params): Observable<unknown> {
    return this.http.post(`/v2${this.apiRoute}-delete`, params);
  }

  public oldRemoveBatchDepartures(
    batchId: number,
    params: Params
  ): Observable<unknown> {
    return this.http.post(
      `/v2${this.apiRoute}/${batchId}/item/batch-delete`,
      params
    );
  }

  public commitBatch(id: number): Observable<Batch> {
    return this.http.post<Batch>(`/v2${this.apiRoute}/${id}/commit`, {});
  }

  public parse(params: Params): Observable<BatchParseDto> {
    return this.http
      .post<BatchParseDto>(`/v2${this.apiRoute}/parse`, params)
      .pipe(
        tap((res: BatchParseDto) => {
          this.batchesParseStorageService.adapter(res);
        })
      );
  }

  public calculate(
    batchId: number,
    params: Params
  ): Observable<PrecalculateItemsResp> {
    const body = {
      ...params,
      recipient_contact_widget_data:
        this.belpostToShortContactAdapterService.transform(
          params.recipient_contact_widget_data
        ),
    };

    return this.http.post<PrecalculateItemsResp>(
      `/v2${this.apiRoute}/${batchId}/precalculate`,
      body
    );
  }

  public updateTracking(s10Code: string): Observable<TrackingStep[]> {
    return this.http
      .get<{
        data: Array<{ steps: TrackingStep[] }>;
      }>(`/v2/tracking/${s10Code}`)
      .pipe(map((res) => res.data[0]?.steps || []));
  }

  public duplicate(id: number, type: DuplicateType): Observable<Batch> {
    return this.http.post<Batch>(`/v2${this.apiRoute}/${id}/${type}`, {});
  }

  public addAttachment(
    batchId: string,
    departureId: string,
    body: AddAttachmentBody
  ): Observable<DepartureAttachmentItem> {
    return this.http.post<DepartureAttachmentItem>(
      `/v1${this.apiRoute}/${batchId}/item/${departureId}/partial-receipts`,
      body
    );
  }

  public deleteDepartureAttachments(
    batchId: number,
    departureId: number,
    params: Params
  ) {
    return this.http.post(
      `/v1${this.apiRoute}/${batchId}/item/${departureId}/partial-receipts-delete-list`,
      params
    );
  }

  public editDepartureAttachment(
    params: EditDepartureAttachmentParams,
    body: AddAttachmentBody
  ): Observable<DepartureAttachmentItem> {
    return this.http.patch<DepartureAttachmentItem>(
      `/v1${this.apiRoute}/${params.batchId}
      /item/${params.departureId}/partial-receipts/${params.attachmentId}`,
      body
    );
  }

  public getDepartureAttachmentById(
    params: EditDepartureAttachmentParams
  ): Observable<DepartureAttachmentItem> {
    return this.http.get<DepartureAttachmentItem>(
      `/v1${this.apiRoute}/${params.batchId}/item/${params.departureId}/partial-receipts/${params.attachmentId}`
    );
  }

  public getDepartureAttachments(
    batchId: string,
    departureId: string,
    search?: string
  ): Observable<DepartureAttachmentItem[]> {
    return this.http.get<DepartureAttachmentItem[]>(
      `/v1${this.apiRoute}/
      ${batchId}/item/${departureId}/partial-receipts`,
      {
        params: createHttpParams({
          search,
        }),
      }
    );
  }

  public generateAllBatchBlanks(batchId: number): Observable<Batch> {
    return this.http.post<Batch>(
      `/v1${this.apiRoute}/${batchId}/generate-blank `,
      {}
    );
  }

  public generateBatchDocument(
    params: GenerateBatchDocumentParams
  ): Observable<BatchMailing> {
    return this.http.post<BatchMailing>(
      `/v2${this.apiRoute}/${params.batchId}/item/${params.departureId}/generate-document `,
      params.labelSize
        ? {
            label_size: params.labelSize,
            document_type: params.documentType,
          }
        : {
            document_type: params.documentType,
          }
    );
  }

  public getLabelSizes(): Observable<LabelSizeList> {
    return this.http
      .get<LabelSizeList>(`/v2/label-sizes`)
      .pipe(tap((res) => this.batchesParseStorageService.labelSizes.set(res)));
  }
}
